import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import EChartCommon from '@op/components/EChartCommon';
import { reviewsFormat, yAxisFormat } from '@op/utils/value';
import { measureTextWidth } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/Channel';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { getPercentageInfo } from '@op/utils';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { NO_DATE } from '@op/constants'; 
import { DBO_V } from '@op/utils/format';
import { getPositiveNegativeIconName } from '@op/pages/Performance/components/MentionsSentiment/chart';

interface ISentimentSBar {
  height: number;
  chartData: any;
  type?: string;
  selectedLanguage: string;
  setSelectedLanguage: (lang: string) => void;
  translate: (value: string) => string;
  sortBy: string;
  setSortBy: (value: string) => void;
}

export default function LanguageBar(props: ISentimentSBar) {
  const { height, chartData, type, setSelectedLanguage, translate } = props;
  const { formatMessage } = useIntl();
  const { dataY = [], negative, positive, total } = chartData || {};
  const totalSum = total.reduce((sum: number, num: number) => sum + num, 0);

  const options = useMemo(() => {
    const maxLabelWidth =
      Math.max(...(measureTextWidth(dataY, type) || [])) || 60;
    const rounded = Math.round(maxLabelWidth / 10) * 10;
    const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 10;
    const isMoreData = dataY.length > 10;
    const options = {
      grid: {
        left: 0,
        right: isMoreData ? 20 : 0,
        contentLabel: true,
      },
      tooltip: {
        extraCssText: 'z-index:8',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          const total = params?.[0]?.data?.total || 0;
          return formatChartParamsDate({
            params,
            total: total,
            // isSort: true,
            beforeSort: false,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return getPositiveNegativeIconName(name, formatMessage);
            },
            valueFormat: (value: number) => reviewsFormat(Math.abs(value), 2),
            // textShowFun: textShowFun(formatMessage),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share =
                  item.originValue === 0
                    ? ''
                    : `(${getPercentageInfo(
                      Math.abs(item.originValue),
                      total,
                      2,
                    )})`;
                return {
                  ...item,
                  value: item.value + share,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              newRecords.reverse();
              const totalPercent = DBO_V.percentage(total, totalSum, 2);

              newRecords.unshift({
                text: `<hr/>`,
                value: '<hr/>',
                share: '',
              });
              newRecords.unshift({
                text: formatMessage(
                  { id: '{language} All Reviews/Total' },
                  { language: `${translate(params[0]?.name)}` },
                ),
                value: totalPercent,
                share: '100%',
              });
              newRecords.unshift({
                text: formatMessage(
                  { id: '{language} All Reviews' },
                  { language: `${translate(params[0]?.name)}` },
                ),
                value: reviewsFormat(total),
                share: '100%',
              });
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: `<span class="echart-tooltip">${translate(
                    params[0]?.name,
                  )}<span>`,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: '',
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: [
          {
            name: 'All Reviews Negative%',
            itemStyle: { color: '#F17C86' },
          },
          {
            name: 'All Reviews Positive%',
            itemStyle: { color: '#69D19C' },
          },
        ],
        textStyle: {
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamPositive,
              },
            },
            negative: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamNegative,
              },
            },
          },
        },
        formatter(name: string) {
          const newName = formatMessage({ id: `${name}` || NO_DATE.EMPTY });
          if (name.includes('Positive')) {
            return `{positive|} ${newName}`;
          }
          if (name.includes('Negative')) {
            return `{negative|} ${newName}`;
          }
          return newName;
        },
      },
      xAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          formatter: (value: number) => yAxisFormat(Math.abs(value)), // 负值转成正值
        },
        boundaryGap: ['10%', 0],
      },
      yAxis: [
        {
          type: 'category',
          position: 'left',
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (value: any) => `{label|${translate(value)}}`,
            rich: { label: { align: 'left' } },
            padding: [0, 0, 0, 0],
            overflow: 'break',
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            width,
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: { show: false },
          axisTick: { show: false },
          data: dataY,
          // inverse: true,
        },
        {
          type: 'category',
          position: 'right',
          data: total,
          axisLabel: {
            formatter: (value: number) => {
              const percent = DBO_V.percentage(value, totalSum, 2);
              return `{value|${reviewsFormat(value)}(${percent})}`;
            },
            show: true,
            width: 120,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          // inverse: true,
        },
      ],
      series: [
        {
          name: 'All Reviews Negative%',
          type: 'bar',
          stack: 'total',
          color: '#F17C86',
          barMaxWidth: 20,
          label: {
            show: false,
          },
          emphasis: {
            disable: true,
          },
          data: negative,
        },
        {
          name: 'All Reviews Positive%',
          type: 'bar',
          stack: 'total',
          color: '#69D19C',
          barMaxWidth: 20,
          label: {
            show: true,
            position: 'right',
            formatter: (params: {
              value: number;
              dataIndex: number;
              data: { total: number };
            }) => {
              const total = params.data.total || 0;
              const value = Math.abs(params?.value) || 0;
              if (value) {
                return getPercentageInfo(value, total, 2);
              }
              return '';
            },
          },
          emphasis: {
            disable: true,
          },
          data: positive,
        },
      ],
    };

    if (isMoreData) {
      return {
        ...options,
        dataZoom: [
          {
            type: 'inside', // 内置型 dataZoom，支持鼠标滚轮缩放
            yAxisIndex: [0, 1],
            filterMode: 'none',
            maxValueSpan: 9,
            minValueSpan: 9,
            startValue: 100,
            endValue: 100,
          },
          {
            type: 'slider',
            filterMode: 'none',
            show: true,
            yAxisIndex: [0, 1],
            width: 0,
            maxValueSpan: 9,
            minValueSpan: 9,
            startValue: 0,
            endValue: 100,
            right: 10,
            showDetail: false,
            showDataShadow: false,
            moveHandleSize: 8,
            moveHandleIcon: 'none',
            zoomLock: true,
            borderColor: 'rgba(0,0,0,0)',
            moveHandleStyle: { color: '#D8DAE4', borderCap: 'square' },
            emphasis: { moveHandleStyle: { color: '#D8DAE4' } },
          },
        ],
      };
    }

    return options;
  }, [chartData, type, formatMessage]);

  const eventsDict = useMemo(() => {
    return {
      click: (params: any) => {
        if (params.name) {
          setSelectedLanguage(params.name);
        }
      },
    };
  }, []);

  return (
    <EChartCommon
      height={height}
      leftTitle={
        <div className="dbo-all-reviews-chart--left-title">
          <span className="left-title--text">
            {formatMessage({ id: 'Language' })}
          </span>
        </div>
      }
      rightTitle={formatMessage({ id: 'All Reviews' })}
      options={options}
      optionsProps={{ type: 'bar' }}
      eventsDict={eventsDict}
      className={'dbo-all-reviews-chart'}
    ></EChartCommon>
  );
}
